/**
 * The authenticated frame. A thin wrapper over `AppShell` from `@wend/ui` that
 * supplies the admin panel's navigation and the signed-in staff block — the
 * shell itself is router-agnostic, so the link component is injected here.
 */
import { Building2, Inbox, LogOut } from 'lucide-react';
import { useState, type ReactNode } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';

import { AppShell, Button, useToast } from '@wend/ui';

import { useAdminAuth } from '../auth/AuthProvider';
import { describeApiError, toApiError } from '../lib/api-error';

export interface AdminShellProps {
  title: ReactNode;
  /** Top-bar actions, aligned right. */
  actions?: ReactNode;
  children: ReactNode;
}

export function AdminShell({ title, actions, children }: AdminShellProps) {
  const { admin, signOut } = useAdminAuth();
  const { pathname } = useLocation();
  const navigate = useNavigate();
  const { toast } = useToast();
  const [signingOut, setSigningOut] = useState(false);

  const handleSignOut = async (): Promise<void> => {
    setSigningOut(true);
    try {
      await signOut();
      navigate('/login', { replace: true });
    } catch (error: unknown) {
      // The session is still live if the revoke failed, so stay signed in and
      // say so — silently "signing out" locally would leave the cookie valid.
      toast({
        tone: 'error',
        title: 'Could not sign out',
        description: describeApiError(toApiError(error)),
      });
    } finally {
      setSigningOut(false);
    }
  };

  return (
    <AppShell
      title={title}
      actions={actions}
      nav={[
        {
          key: 'admin',
          label: 'Administration',
          items: [
            {
              key: 'tenants',
              label: 'Tenants',
              icon: <Building2 aria-hidden="true" />,
              active: pathname.startsWith('/tenants'),
              render: (props) => <Link to="/tenants" {...props} />,
            },
            {
              key: 'interests',
              label: 'Interests',
              icon: <Inbox aria-hidden="true" />,
              active: pathname.startsWith('/interests'),
              render: (props) => <Link to="/interests" {...props} />,
            },
          ],
        },
      ]}
      sidebarFooter={
        <div className="flex flex-col gap-2">
          <div className="px-2">
            <p className="truncate text-[0.85rem] font-medium">{admin?.name ?? 'Wend staff'}</p>
            <p className="truncate font-mono text-[0.68rem] tracking-[0.06em] text-muted">
              {admin?.email ?? ''}
            </p>
          </div>

          <Button
            type="button"
            variant="ghost"
            size="sm"
            fullWidth
            loading={signingOut}
            leadingIcon={<LogOut aria-hidden="true" />}
            onClick={() => {
              void handleSignOut();
            }}
          >
            Sign out
          </Button>
        </div>
      }
    >
      {children}
    </AppShell>
  );
}
